import { useEffect } from 'react'
import { useAuthStore } from '@/shared/stores/auth.store'
import { chatwootApi } from '../api/chatwoot.api'
import '../types'

const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL
const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN
const SCRIPT_ID = 'chatwoot-sdk'

/**
 * Loads the Chatwoot support widget and identifies the signed-in user against
 * it. Renders nothing — the SDK injects its own launcher into document.body.
 *
 * Identification is HMAC-signed server-side (GET /support/chatwoot-identity),
 * which is what stops a visitor from calling $chatwoot.setUser() with someone
 * else's id and reading their support history. Chatwoot only enforces the
 * signature once "Enforce user identity validation" is on for the inbox.
 *
 * No-ops when either env var is unset, so local/CI runs without a Chatwoot
 * instance are unaffected.
 */
export function ChatwootWidget() {
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    if (!BASE_URL || !WEBSITE_TOKEN || !user) return

    let cancelled = false

    async function identify() {
      if (!user) return
      try {
        const res = await chatwootApi.identity()
        if (cancelled) return
        window.$chatwoot?.setUser(res.data.identifier, {
          name: `${user.first_name} ${user.last_name}`.trim(),
          email: user.email,
          identifier_hash: res.data.identifier_hash,
        })
        window.$chatwoot?.setCustomAttributes({ client: user.active_client.name })
      } catch {
        // Support chat is non-critical: a failed identity call leaves the
        // widget usable as an anonymous visitor rather than breaking the app.
      }
    }

    // The SDK fires chatwoot:ready once $chatwoot exists. The immediate branch
    // covers a remount, where the script is already loaded and the event has
    // long since fired.
    const onReady = () => {
      void identify()
    }
    if (window.$chatwoot) {
      onReady()
    } else {
      window.addEventListener('chatwoot:ready', onReady, { once: true })
    }

    const cleanup = () => {
      cancelled = true
      window.removeEventListener('chatwoot:ready', onReady)
    }

    if (document.getElementById(SCRIPT_ID)) return cleanup

    // hideMessageBubble keeps the launcher off screen entirely. Support is reached
    // from the "Start a chat" card on the profile page (openSupportChat), so it is
    // deliberate rather than one click away. Without this the bubble floats over
    // every page in the app.
    window.chatwootSettings = {
      position: 'right',
      type: 'standard',
      darkMode: 'auto',
      hideMessageBubble: true,
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `${BASE_URL}/packs/js/sdk.js`
    script.defer = true
    script.async = true
    script.onload = () => {
      window.chatwootSDK?.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL })
    }
    document.head.appendChild(script)

    return cleanup
  }, [user])

  return null
}
